<script setup lang="ts">
import type { PropType } from 'vue';
import { computed, onMounted, ref, toRefs } from 'vue';
import { useMediaQuery } from '#imports';
import type { RegistrationStage } from '~/pages/registration/components/registration-stages.vue';

const props = defineProps({
  stage: {
    type: String as PropType<RegistrationStage>,
    required: true,
  },
});

const { stage } = toRefs(props);

const stages: { name: RegistrationStage, title: string }[] = [
  {
    name: 'profile',
    title: 'Registration Info',
  },
  {
    name: 'company',
    title: 'Company Info',
  },
  {
    name: 'captcha',
    title: 'Captcha',
  },
];

const isMediumScreen = useMediaQuery('(min-width: 768px)');

const stagesRef = ref<HTMLDivElement[] | null>(null);
const stageWidth = ref(0);

onMounted(() => {
  console.log('stagesRef: ', stagesRef.value);

  if (stagesRef.value) {
    stageWidth.value = stagesRef.value[0].clientWidth;
  }
});

const offset = computed(() => {
  const index = stages.findIndex(({ name }) => name === stage.value);

  let gap = 24;

  if (!isMediumScreen.value) {
    gap = 16;
  }

  return index * stageWidth.value + (index) * gap;
});

function activeBg(stageName: string, idx: number) {
  return stage.value === stageName && !isMediumScreen.value;
}

const stagesActiveBg = computed(() => {
  return stages.map(({ name }, idx) => {
    return activeBg(name, idx);
  });
});
</script>

<template>
  <div class="flex gap-4 lg:gap-6 relative w-max md:w-184 lg:w-177 overflow-x-hidden md:overflow-x-initial">
    <div
      v-if="isMediumScreen"
      class="h-full bg-green-light absolute transition-transform rounded-lg hidden md:block"
      :style="`transform: translateX(${offset}px); width: ${stageWidth}px`"
    />

    <div
      v-for="(stageData, idx) in stages"
      :key="stageData.name"
      ref="stagesRef"
      class="flex flex-col w-72.5 md:w-auto md:flex-1 lg:w-55 relative px-6 py-5 rounded-lg transition-transform"
      :class="{
        'bg-green-light md:bg-transparent': stagesActiveBg[idx],
      }"
      :style="!isMediumScreen && `transform: translateX(-${offset}px)`"
    >
      <span
        :class="
          stageData.name === stage
            ? 'c-text-regular'
            : 'c-text-inactive'
        "
      >
        Step {{ idx + 1 }}
      </span>

      <Component
        :is="stageData.name === stage ? 'h1' : 'h3'"
        class="c-text-dark text-h6 mt-2"
      >
        {{ stageData.title }}
      </Component>

      <span
        class="mt-2.5"
        :class="
          stageData.name === stage
            ? 'c-green-pure'
            : 'c-text-inactive'
        "
      >
        {{ stageData.name === stage ? 'In progress...' : 'Pending' }}
      </span>
    </div>
  </div>
</template>
